/**
 * Боковая панель: название, поиск по сети, разделы, профиль.
 *
 * В светлой теме светлая, в тёмной — тёмно-синяя.
 * На планшете сворачивается до значков, на телефоне становится
 * полосой сверху. В свёрнутом виде поиск открывается отдельной
 * всплывающей панелью по значку.
 */
import { useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { useSelector } from 'react-redux';

import { selectUser } from '../../store/slices/authSlice';
import { ROLE_LABELS, badgeFor, REQUESTS_PAGE_ROLES } from '../../domain/netboxRequests';
import { selectNetboxCounts } from '../../store/slices/netboxSlice';
import GlobalSearch from './GlobalSearch';
import { LogoMark } from '../common/Logo';
import Icon from '../common/Icon';
import styles from './NavBar.module.css';

/** Разделы приложения. Порядок совпадает с прежней версией. */
const SECTIONS = [
  { to: '/dashboard', label: 'Дашборд',      icon: 'dashboard' },
  { to: '/objects',   label: 'Объекты',      icon: 'objects' },
  { to: '/dupes',     label: 'Дубли SN',     icon: 'dupes' },
  { to: '/divisions', label: 'Дивизионы',    icon: 'divisions' },
  { to: '/equipment', label: 'Оборудование', icon: 'equipment' },
  { to: '/racks',     label: 'Шкафы',        icon: 'racks' },
  { to: '/ports',     label: 'Порты',        icon: 'ports' },
  // Координатору, контролёру данных и разработчику
  { to: '/netbox',    label: 'Запросы в NetBox', icon: 'netbox', roles: REQUESTS_PAGE_ROLES },
];

/** Инициалы для значка профиля: «Маймистов В.» → «МВ». */
function initials(name) {
  const parts = String(name || '').replace(/\./g, ' ').split(/\s+/).filter(Boolean);
  return (parts[0]?.[0] || '?').toUpperCase() + (parts[1]?.[0] || '').toUpperCase();
}

export default function NavBar({ onOpenCabinet }) {
  const user = useSelector(selectUser);
  const counts = useSelector(selectNetboxCounts);
  const badge = badgeFor(user, counts);
  const sections = SECTIONS.filter((s) => !s.roles || s.roles.includes(user?.role));
  const [searchOpen, setSearchOpen] = useState(false);
  const tabsRef = useRef(null);
  const { pathname } = useLocation();

  // На телефоне разделы — полоса с прокруткой вбок: открытый раздел
  // не должен оставаться за краем или под кнопкой поиска
  useEffect(() => {
    const box = tabsRef.current;
    const active = box?.querySelector('[aria-current="page"]');
    if (!box || !active || box.scrollWidth <= box.clientWidth) return;
    const outer = box.getBoundingClientRect();
    const tab = active.getBoundingClientRect();
    const gap = 8;
    if (tab.right > outer.right - gap) box.scrollLeft += tab.right - outer.right + gap;
    else if (tab.left < outer.left + gap) box.scrollLeft -= outer.left + gap - tab.left;
  }, [pathname]);

  // Всплывающий поиск закрывается клавишей Escape
  useEffect(() => {
    if (!searchOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSearchOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [searchOpen]);

  const name = user?.displayName || user?.username || '';

  return (
    <nav className={styles.nav} aria-label="Разделы">
      <div className={styles.brand}>
        <LogoMark size={30} className={styles.brandMark} title="Switch Inspector" />
        <span className={styles.brandFull} aria-hidden="true">Switch Inspector</span>
      </div>

      <div className={styles.searchFull}>
        <GlobalSearch variant="sidebar" />
      </div>

      <div className={styles.tabs} ref={tabsRef}>
        <button
          type="button"
          className={`${styles.tab} ${styles.searchButton}`}
          onClick={() => setSearchOpen((v) => !v)}
          aria-expanded={searchOpen}
          title="Поиск по всей сети"
        >
          <Icon name="search" />
          <span className={styles.tabLabel}>Поиск</span>
        </button>

        {sections.map((section) => (
          <NavLink
            key={section.to}
            to={section.to}
            title={section.label}
            className={({ isActive }) => (isActive ? `${styles.tab} ${styles.tabActive}` : styles.tab)}
          >
            <Icon name={section.icon} />
            <span className={styles.tabLabel}>{section.label}</span>
            {section.to === '/netbox' && badge > 0 && (
              <span
                className={styles.tabBadge}
                title={user.role === 'controller' ? 'Запросов на проверке' : 'Объектов готово к отправке'}
              >
                {badge}
              </span>
            )}
          </NavLink>
        ))}
      </div>

      {/* Телефон: поиск справа, у профиля, — не рядом со знаком-линзой,
          чтобы знак не принимали за вторую кнопку поиска */}
      <button
        type="button"
        className={`${styles.tab} ${styles.searchButtonEnd}`}
        onClick={() => setSearchOpen((v) => !v)}
        aria-expanded={searchOpen}
        title="Поиск по всей сети"
      >
        <Icon name="search" />
        <span className={styles.tabLabel}>Поиск</span>
      </button>

      {user && (
        <button className={styles.user} onClick={onOpenCabinet} title="Личный кабинет">
          {user.avatar
            ? <span className={styles.avatar} style={{ backgroundImage: `url(${user.avatar})` }} />
            : <span className={styles.avatar}>{initials(name)}</span>}
          <span className={styles.userText}>
            <span className={styles.userName}>{name}</span>
            {user.role && <span className={styles.userRole}>{ROLE_LABELS[user.role]}</span>}
          </span>
        </button>
      )}

      {searchOpen && (
        <>
          <button className={styles.scrim} onClick={() => setSearchOpen(false)} aria-label="Закрыть поиск" />
          <div className={styles.searchPopover} role="dialog" aria-label="Поиск по всей сети">
            <GlobalSearch variant="popover" onNavigate={() => setSearchOpen(false)} />
          </div>
        </>
      )}
    </nav>
  );
}
